/* eslint-disable react/require-default-props */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import PropTypes from 'prop-types';

import TooltipContent from './TooltipContent';
import { TOOLTIP_CONTENT_POSITION } from '../../../constants/PropTypesShapes';

/*
    Tooltip react based component with ADA.
    In this component we are placing the tooltip content based on the tooltip position from props or fixed position
    which is in styling.

    This component doesn't support to position the tooltip arrow icon dynamically - Need to implement this later.

    tooltipContentPosition {x:50, y:-120}  with x and y values are used to place tooltip content based on the
    tooltip icon place to position tooltip container to fit all resolutions.

 */

let tooltipIdCounter = 0;

class AriaTooltipComponent extends React.Component {
  // create unique id so multiple tooltips can be used in the same view
  static uniqueID(prefix) {
    const id = `${tooltipIdCounter += 1}`;
    return prefix ? prefix + id : id;
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      active: false,
      direction: props.direction,
      id: props.id || AriaTooltipComponent.uniqueID('pp-tooltip-'),
    };

    this.handleMouseOver = this.handleMouseOver.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  handleClick() {
    this.setState({ active: true });
  }

  handleMouseOver() {
    this.setState({ active: true });
  }

  handleMouseLeave() {
    this.setState({ active: false });

    // Perform any callback function after tooltip hides
    if (this.props.afterHide && typeof this.props.afterHide === 'function') this.props.afterHide();
  }

  handleFocus() {
    this.handleClick();
  }

  // adds tooltip 'aria-describedby', 'aria-labelledby', 'aria-controls' attribute to child element
  addAriaLabelsToTooltip(tooltipID) {
    return React.Children.map(this.props.children, (e) => React.cloneElement(e, {
      'aria-describedby': tooltipID,
      'aria-labelledby': tooltipID,
      'aria-controls': tooltipID,
    }));
  }

  render() {
    const {
      message, direction, className, tooltipContentPosition,
    } = this.props;
    const { active } = this.state;
    const containerClass = `pp-tooltip-wrapper ${className} ${active ? 'active' : ''}`;
    const tooltipID = this.state.id;

    const attr = {
      role: 'tooltip',
      className: containerClass,
      id: tooltipID,
    };

    if (this.props.eventType === 'hover') {
      attr.onMouseOver = this.handleMouseOver;
      attr.onMouseLeave = this.handleMouseLeave;
      attr.onBlur = this.handleMouseLeave;
      attr.onFocus = this.handleFocus;
    } else {
      attr.onClick = () => this.handleClick();
    }

    return (
      <div {...attr}>
        <TooltipContent
          message={message}
          direction={direction}
          active={active}
          tooltipContentPosition={tooltipContentPosition}
        />
        {this.addAriaLabelsToTooltip(tooltipID)}
      </div>
    );
  }
}

AriaTooltipComponent.displayName = 'AriaTooltipComponent';

AriaTooltipComponent.defaultProps = {
  direction: 'top',
  eventType: 'hover',
  className: '',
};

AriaTooltipComponent.propTypes = {
  message: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.element,
  ]).isRequired,
  direction: PropTypes.oneOf(['top', 'bottom', 'left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right']),
  children: PropTypes.node,
  eventType: PropTypes.oneOf(['hover', 'click']),
  id: PropTypes.string,
  className: PropTypes.string,
  afterHide: PropTypes.func, // this callback function is used for triggering FPTI events or any other events for tooltip
  tooltipContentPosition: PropTypes.shape(TOOLTIP_CONTENT_POSITION), // some thing like this {x:50, y:-120}
};

export default AriaTooltipComponent;
